// src/pages/ChatBot/evals/EvalsPanel.tsx
//
// Evaluation tab of the Manage Chat console. Orchestrates two stacked sections:
//   1. Golden Set — the curated eval corpus (golden-set.tsx), which reports its
//      readiness up so this panel can gate the Run button.
//   2. Evaluation — starts a run over the golden set and displays results. A
//      run is asynchronous: POST returns a runId, then this panel polls the run
//      doc every few seconds until it leaves 'running'. Past runs list below;
//      the newest completed run auto-loads on mount.
//
// This file owns the run lifecycle and the polling state machine. The golden set
// table lives in golden-set.tsx, the run-confirm dialog in run-eval-modal.tsx,
// and the heavy per-run display in run-detail.tsx.

import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { formatDateTime } from '../query-logs/derive';
import { EvalApiError, fetchEvalRun, fetchEvalRuns, startEvalRun } from './api';
import type { EvalRunDoc, EvalRunSummary } from './types';
import GoldenSetSection, { EMPTY_GOLDEN_STATS } from './golden-set';
import type { GoldenStats } from './golden-set';
import RunEvalConfirmModal from './run-eval-modal';
import RunDetail from './run-detail';
import './EvalsPanel.css';

// Poll cadence for an in-progress run.
const POLL_INTERVAL_MS = 3000;

// ── Runs section ────────────────────────────────────────────────────────────

function statusPillClass(status: EvalRunSummary['status']): string {
  if (status === 'completed') return 'eval-run-pill-completed';
  if (status === 'failed') return 'eval-run-pill-failed';
  return 'eval-run-pill-running';
}

// ── Panel ───────────────────────────────────────────────────────────────────

export default function EvalsPanel() {
  const { isAuthenticated } = useAuth();

  // Golden set readiness, reported up from GoldenSetSection to gate the Run
  // button. setGoldenStats is a stable setter, so passing it as onStats can't
  // churn the section's fetch effect.
  const [goldenStats, setGoldenStats] = useState<GoldenStats>(EMPTY_GOLDEN_STATS);

  const [runs, setRuns] = useState<EvalRunSummary[]>([]);
  const [runsError, setRunsError] = useState('');
  const [runsReloadToken, setRunsReloadToken] = useState(0);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [runDoc, setRunDoc] = useState<EvalRunDoc | null>(null);
  const [runDocError, setRunDocError] = useState('');
  const [isRunDocLoading, setIsRunDocLoading] = useState(false);
  const [pollRunId, setPollRunId] = useState<string | null>(null);
  const [isStartingRun, setIsStartingRun] = useState(false);
  const [isRunConfirmOpen, setRunConfirmOpen] = useState(false);
  const [runBanner, setRunBanner] = useState<{ kind: 'info' | 'warn'; text: string } | null>(null);

  // Mirror of selectedRunId for the polling effect to read without re-subscribing
  // (re-subscribing on every selection change would reset the interval).
  const selectedRunIdRef = useRef<string | null>(null);
  useEffect(() => {
    selectedRunIdRef.current = selectedRunId;
  }, [selectedRunId]);

  // Runs list + auto-select. On (re)load: an in-progress run becomes the polled
  // and displayed run; otherwise the newest completed run auto-loads. The
  // `prev ?? …` guards make this a no-op once a run is already selected/polled,
  // so a refetch never yanks the user's current selection.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let cancelled = false;
    fetchEvalRuns()
      .then(({ runs: list }) => {
        if (cancelled) return;
        setRuns(list);
        setRunsError('');
        const running = list.find((run) => run.status === 'running');
        if (running) {
          setPollRunId((prev) => prev ?? running.id);
          setSelectedRunId((prev) => prev ?? running.id);
        } else {
          const newestCompleted = list.find((run) => run.status === 'completed');
          setSelectedRunId((prev) => prev ?? newestCompleted?.id ?? null);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setRunsError(err instanceof Error ? err.message : 'Failed to load evaluation runs.');
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, runsReloadToken]);

  // Load the selected run's full document — except while it is the actively
  // polled run, whose updates the poll effect owns (so the two never race or
  // double-fetch). The loading flag only shows when switching to a run we do
  // not already hold, avoiding a flicker when the poll completes.
  useEffect(() => {
    if (!isAuthenticated || !selectedRunId) return undefined;
    if (selectedRunId === pollRunId) return undefined;
    let cancelled = false;
    setRunDocError('');
    setIsRunDocLoading(runDoc?.id !== selectedRunId);
    fetchEvalRun(selectedRunId)
      .then(({ run }) => {
        if (!cancelled) setRunDoc(run);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setRunDocError(err instanceof Error ? err.message : 'Failed to load the run.');
        }
      })
      .finally(() => {
        if (!cancelled) setIsRunDocLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // runDoc is read only to suppress the loading flicker; it is intentionally
    // not a dependency (depending on it would refetch on every doc update).
  }, [isAuthenticated, selectedRunId, pollRunId]);

  // Polling: while pollRunId is set, fetch the run doc every POLL_INTERVAL_MS
  // (plus once immediately) until it leaves 'running'. Live progress reflects
  // into the display only when the polled run is the one on screen, so viewing
  // a past run mid-poll is never clobbered. On completion the poll stops and
  // refreshes the runs list to pick up the final status + headline.
  useEffect(() => {
    if (!pollRunId) return undefined;
    let cancelled = false;

    const tick = () => {
      fetchEvalRun(pollRunId)
        .then(({ run }) => {
          if (cancelled) return;
          if (selectedRunIdRef.current === run.id) setRunDoc(run);
          if (run.status !== 'running') {
            setPollRunId(null);
            setRunsReloadToken((token) => token + 1);
          }
        })
        .catch(() => {
          // Transient poll error — keep polling; the runs list surfaces failures.
        });
    };

    tick();
    const interval = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [pollRunId]);

  // Fires only after the confirm modal is accepted. The modal always closes when
  // the request settles (finally), whether it started the run or surfaced a
  // banner (409 already-running / 422 empty set / other failure).
  const handleConfirmRun = async () => {
    setRunBanner(null);
    setIsStartingRun(true);
    try {
      const { runId } = await startEvalRun();
      setSelectedRunId(runId);
      setPollRunId(runId);
      setRunsReloadToken((token) => token + 1);
    } catch (err) {
      if (err instanceof EvalApiError && err.status === 409) {
        // A run is already in progress — surface it and pick it up via a list
        // refresh, which starts polling the existing run.
        setRunBanner({ kind: 'info', text: 'An evaluation is already running.' });
        setRunsReloadToken((token) => token + 1);
      } else if (err instanceof EvalApiError && err.status === 422) {
        setRunBanner({
          kind: 'warn',
          text: err.message || 'The golden set has no ready entries to evaluate.',
        });
      } else {
        setRunBanner({
          kind: 'warn',
          text: err instanceof Error ? err.message : 'Failed to start the evaluation.',
        });
      }
    } finally {
      setIsStartingRun(false);
      setRunConfirmOpen(false);
    }
  };

  // isAuthenticated is boolean | null; null means auth is still bootstrapping —
  // render nothing rather than a misleading logged-out message.
  if (isAuthenticated === null) return null;
  if (!isAuthenticated) {
    return (
      <div className="eval-empty">
        You must be logged in as an admin to view evaluation.
      </div>
    );
  }

  const isRunInProgress = pollRunId !== null || runs.some((run) => run.status === 'running');
  // A zero ready-count only disables the Run button when we can see the whole
  // set (page 0, no further pages); with more pages unseen, allow the click and
  // let the backend's 422 be authoritative.
  const noReadyEntries = goldenStats.wholeSetLoaded && goldenStats.readyCount === 0;
  const isRunDisabled = isRunInProgress || isStartingRun || noReadyEntries;
  const runButtonTitle = isRunInProgress
    ? 'An evaluation is already running.'
    : noReadyEntries
      ? 'Add a golden query with a custom answer first — runs only score ready entries.'
      : 'Run the evaluation over the golden set.';

  return (
    <div className="eval-panel">
      <GoldenSetSection onStats={setGoldenStats} />

      <section className="eval-section">
        <div className="eval-section-head">
          <div className="eval-section-title">Evaluation</div>
          <button
            type="button"
            className="eval-btn-run"
            onClick={() => setRunConfirmOpen(true)}
            disabled={isRunDisabled}
            title={runButtonTitle}
          >
            {isStartingRun && <span className="eval-spinner" />}
            Run Evaluation
          </button>
        </div>

        {runBanner && (
          <div className={`eval-banner eval-banner-${runBanner.kind}`}>{runBanner.text}</div>
        )}
        {runsError && <div className="eval-error">{runsError}</div>}

        <RunDetail run={runDoc} isLoading={isRunDocLoading} error={runDocError} />

        {runs.length > 0 && (
          <div className="eval-runs-list">
            <div className="eval-subhead">Past runs</div>
            {runs.map((run) => (
              <button
                key={run.id}
                type="button"
                className={`eval-run-item${run.id === selectedRunId ? ' eval-run-item-selected' : ''}`}
                onClick={() => {
                  setRunBanner(null);
                  setSelectedRunId(run.id);
                }}
              >
                <span className={`eval-run-pill ${statusPillClass(run.status)}`}>
                  {run.status}
                </span>
                <span className="eval-run-time">{formatDateTime(run.startedAt)}</span>
                {run.headline && (
                  <span className="eval-run-headline">
                    {run.headline.recallMean !== undefined &&
                      `recall ${run.headline.recallMean.toFixed(3)}`}
                    {run.headline.recallMean !== undefined &&
                      run.headline.meanCorrectness !== undefined &&
                      ' · '}
                    {run.headline.meanCorrectness !== undefined &&
                      `correctness ${run.headline.meanCorrectness.toFixed(2)}`}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </section>

      <RunEvalConfirmModal
        open={isRunConfirmOpen}
        isStarting={isStartingRun}
        onConfirm={handleConfirmRun}
        onCancel={() => setRunConfirmOpen(false)}
      />
    </div>
  );
}
